import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ProductsPage from "@/pages/ProductsPage";
import TrackOticPage from "@/pages/TrackOticPage";
import { AuthProvider } from "@/context/AuthContext";
import { TrackOticAuthProvider } from "@/context/TrackOticAuthContext";

// Control Portal (SentinelX Admin)
import ControlLayout from "@/pages/control/ControlLayout";
import ControlLoginPage from "@/pages/control/ControlLoginPage";
import ControlDashboard from "@/pages/control/ControlDashboard";
import TenantListPage from "@/pages/control/TenantListPage";
import TenantDetailPage from "@/pages/control/TenantDetailPage";
import TenantLocationsPage from "@/pages/control/TenantLocationsPage";
import TenantUsersPage from "@/pages/control/TenantUsersPage";
import TenantIntegrationsPage from "@/pages/control/TenantIntegrationsPage";
import TenantBillingPage from "@/pages/control/TenantBillingPage";
import GlobalAuditPage from "@/pages/control/GlobalAuditPage";
import GlobalAlertsPage from "@/pages/control/GlobalAlertsPage";
import GlobalBillingPage from "@/pages/control/GlobalBillingPage";
import SettingsPage from "@/pages/control/SettingsPage";
import DeviceInventoryPage from "@/pages/control/DeviceInventoryPage";
import DeviceDetailPage from "@/pages/control/DeviceDetailPage";
import PlanBuilderPage from "@/pages/control/PlanBuilderPage";
import DeviceControlPage from "@/pages/control/DeviceControlPage";

// Customer Ops Portal (Tenant Operations)
import OpsLayout from "@/pages/ops/OpsLayout";
import OpsLoginPage from "@/pages/ops/OpsLoginPage";
import OpsDashboard from "@/pages/ops/OpsDashboard";
import WorkOrdersPage from "@/pages/ops/WorkOrdersPage";
import WorkOrderDetailPage from "@/pages/ops/WorkOrderDetailPage";
import LiveTrackingPage from "@/pages/ops/LiveTrackingPage";
import DeviceHealthPage from "@/pages/ops/DeviceHealthPage";
import OpsAlertsPage from "@/pages/ops/OpsAlertsPage";
import OpsReportsPage from "@/pages/ops/OpsReportsPage";
import OpsUsersPage from "@/pages/ops/OpsUsersPage";

// Layout wrapper that conditionally shows Navbar/Footer
function MainLayout({ children }) {
  const location = useLocation();
  const isAppRoute = location.pathname.startsWith('/control') || 
                     location.pathname.startsWith('/ops') ||
                     location.pathname.startsWith('/app');
  
  if (isAppRoute) {
    return <>{children}</>;
  }
  
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <TrackOticAuthProvider>
          <div className="App min-h-screen bg-background text-foreground">
            <BrowserRouter>
              <MainLayout>
                <Routes>
                  {/* Marketing Site Routes */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/trackotic" element={<TrackOticPage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/case-studies" element={<CaseStudiesPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  
                  {/* Control Portal (SentinelX Admin) */}
                  <Route path="/control/login" element={<ControlLoginPage />} />
                  <Route path="/control" element={<ControlLayout />}>
                    <Route index element={<Navigate to="/control/dashboard" replace />} />
                    <Route path="dashboard" element={<ControlDashboard />} />
                    <Route path="tenants" element={<TenantListPage />} />
                    <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
                    <Route path="tenants/:tenantId/locations" element={<TenantLocationsPage />} />
                    <Route path="tenants/:tenantId/users" element={<TenantUsersPage />} />
                    <Route path="tenants/:tenantId/integrations" element={<TenantIntegrationsPage />} />
                    <Route path="tenants/:tenantId/billing" element={<TenantBillingPage />} />
                    <Route path="audit" element={<GlobalAuditPage />} />
                    <Route path="alerts" element={<GlobalAlertsPage />} />
                    <Route path="billing" element={<GlobalBillingPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="plans" element={<PlanBuilderPage />} />
                    <Route path="devices" element={<DeviceInventoryPage />} />
                    <Route path="devices/:deviceId" element={<DeviceDetailPage />} />
                  </Route>

                  {/* Customer Ops Portal (Tenant Operations) */}
                  <Route path="/ops/login" element={<OpsLoginPage />} />
                  <Route path="/ops" element={<OpsLayout />}>
                    <Route index element={<Navigate to="/ops/dashboard" replace />} />
                    <Route path="dashboard" element={<OpsDashboard />} />
                    <Route path="work-orders" element={<WorkOrdersPage />} />
                    <Route path="work-orders/:id" element={<WorkOrderDetailPage />} />
                    <Route path="tracking" element={<LiveTrackingPage />} />
                    <Route path="devices" element={<DeviceHealthPage />} />
                    <Route path="alerts" element={<OpsAlertsPage />} />
                    <Route path="reports" element={<OpsReportsPage />} />
                    <Route path="users" element={<OpsUsersPage />} />
                  </Route>

                  {/* Legacy app routes - redirect to ops */}
                  <Route path="/app/login" element={<Navigate to="/ops/login" replace />} />
                  <Route path="/app/*" element={<Navigate to="/ops/dashboard" replace />} />
                </Routes>
              </MainLayout>
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </div>
        </TrackOticAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
